import { Column, TemplatesRefMap } from '@miq/catalog';
import { DataObject } from '@shared/models/data-object.model';
import { validateUomFields } from '@shared/utils/helpers/records.helpers';
import { BehaviorSubject, Subject } from 'rxjs';

const getAccountableColumns = cols => cols.filter(col => Boolean(col.value) && Boolean(col.unit));

const validCumulativeColumnUom = (cols: Column[]) => {
  const accountableColumns = getAccountableColumns(cols);
  const [firstCol] = accountableColumns;
  return accountableColumns.every(col => (!!col.unit && col.unit.value === firstCol.unit.value) || !col.unit);
};

export class CumulativeBoxStore {
  private _autoSum = new BehaviorSubject(false);
  public autoSum$ = this._autoSum.asObservable();

  private _errors = new BehaviorSubject([]);
  public errors$ = this._errors.asObservable();

  private _totalColumnChange = new Subject<Column>();
  public totalColumnChange$ = this._totalColumnChange.asObservable();

  private _customTemplateRefMap: TemplatesRefMap;

  constructor(
    public record: DataObject,
    public totalColumn: Column,
    public cumulativeColumns: Column[],
    public isNewRecord: boolean,
    public showAutoSumControl: boolean,
    autoSum: boolean,
    customTemplateRefMap: TemplatesRefMap
  ) {
    if (this._autoSum.value !== !!autoSum) {
      this._autoSum.next(autoSum);
    }
    this._customTemplateRefMap = customTemplateRefMap;
  }

  validateAndUpdateTotalColumn() {
    if (!this.isValid()) {
      return false;
    }

    if (this.getAutoSumStateSnap()) {
      this.calculateTotalsAndPush();
    }

    return true;
  }

  calculateTotalsAndPush() {
    this.totalColumn.value = this.cumulativeColumns
      .filter(col => !isNaN(col.value) && Boolean(col.unit))
      .reduce((acc, col) => acc + col.value, 0);

    if (this.totalColumn.unit) {
      const [firstCol] = this.validCumulativeColumns;
      this.totalColumn.unit.value = firstCol.unit.value;
    }
    this._totalColumnChange.next(this.totalColumn);
  }

  get validCumulativeColumns() {
    const accountableColumns = getAccountableColumns(this.cumulativeColumns);
    return accountableColumns.filter(col => Boolean(col.unit.value) || Boolean(!col.unit));
  }

  public getAutoSumStateSnap() {
    return this._autoSum.value;
  }

  destroy(): void {
    this._autoSum.complete();
    this._errors.complete();
    this._totalColumnChange.complete();
  }

  setAutoSumState(state: boolean) {
    this._autoSum.next(state);
    if (state) {
      this.validateAndUpdateTotalColumn();
    }
  }

  isValid() {
    const errors = [];
    if (!validCumulativeColumnUom(this.cumulativeColumns)) {
      errors.push('recordsSearch.cannotSelectMultipleCurrencies');
    } else if (
      // any uom is invalid
      this.cumulativeColumns.filter(col => Boolean(col.unit)).some(column => !validateUomFields(column, column.unit))
    ) {
      errors.push('recordsSearch.someFieldsInvalid');
    }
    this._errors.next(errors);
    return errors.length === 0;
  }

  getCustomTemplateRefMap(): TemplatesRefMap {
    return this._customTemplateRefMap;
  }
}
